import {
  ORG_ID_ROUTE_TOKEN,
  ORG_SCOPED_ROUTE_PREFIXES,
  ROUTE_SEGMENT,
  type OrgScopedRoutePrefix,
} from './const';

export const isOrgScopedPathname = (pathname: string): boolean =>
  ORG_SCOPED_ROUTE_PREFIXES.some(prefix => pathname.startsWith(`/${prefix}/`));

export const getOrgScopedPrefix = (
  pathname: string
): OrgScopedRoutePrefix | null => {
  for (const prefix of ORG_SCOPED_ROUTE_PREFIXES) {
    if (pathname.startsWith(`/${prefix}/`)) return prefix;
  }

  return null;
};

export const getOrgIdFromPathname = (pathname: string): string | null => {
  const prefix = getOrgScopedPrefix(pathname);
  if (!prefix) return null;

  const rest = pathname.slice(`/${prefix}/`.length);

  return rest.split('/')[0] || null;
};

export const isOrgScopedDetailPathname = (pathname: string): boolean => {
  const prefix = getOrgScopedPrefix(pathname);
  if (!prefix) return false;

  const rest = pathname.slice(`/${prefix}/`.length);
  const [, ...detailSegments] = rest.split('/').filter(Boolean);

  return detailSegments.length > 0;
};

export const replaceOrgInPathname = (
  pathname: string,
  newOrgId: string
): string => {
  const prefix = getOrgScopedPrefix(pathname);
  if (!prefix) return pathname;

  const rest = pathname.slice(`/${prefix}/`.length);
  const segments = rest.split('/');

  segments[0] = newOrgId;

  return `/${prefix}/${segments.join('/')}`;
};

export const resolveOrgSwitchTargetPath = (
  pathname: string,
  newOrgId: string
): string | null => {
  const prefix = getOrgScopedPrefix(pathname);
  if (!prefix) return null;

  if (isOrgScopedDetailPathname(pathname)) {
    return `/${prefix}/${newOrgId}`;
  }

  return replaceOrgInPathname(pathname, newOrgId);
};

export const buildOrgScopedPath = (
  prefix: OrgScopedRoutePrefix,
  orgId: string,
  ...restSegments: string[]
): string => `/${prefix}/${[orgId, ...restSegments].join('/')}`;

export const buildAnalyticsPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.ANALYTICS, orgId, ...restSegments);

export const buildAgentsPath = (
  orgId: string,
  ...restSegments: string[]
): string => buildOrgScopedPath(ROUTE_SEGMENT.AGENTS, orgId, ...restSegments);

export const buildConversationPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.CONVERSATION, orgId, ...restSegments);

export const buildBillingPath = (
  orgId: string,
  ...restSegments: string[]
): string => buildOrgScopedPath(ROUTE_SEGMENT.BILLING, orgId, ...restSegments);

export const buildKnowledgeManagementPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(
    ROUTE_SEGMENT.KNOWLEDGE_MANAGEMENT,
    orgId,
    ...restSegments
  );

export const buildOrgManagementPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.ORG_MANAGEMENT, orgId, ...restSegments);

export const buildCallManagementPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.CALL_MANAGEMENT, orgId, ...restSegments);

export const buildRoleManagementPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.ROLE_MANAGEMENT, orgId, ...restSegments);

export const buildJsonManagementPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.JSON_MANAGEMENT, orgId, ...restSegments);

export const buildModelsManagementPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.MODELS_MANAGEMENT, orgId, ...restSegments);

export const buildSipTrunkPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.SIP_TRUNK, orgId, ...restSegments);

export const buildPromptLibraryPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.PROMPT_LIBRARY, orgId, ...restSegments);

export const buildSipTrunkVendorsPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.SIP_TRUNK_VENDORS, orgId, ...restSegments);

export const buildLivekitServerPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.LIVEKIT_SERVER, orgId, ...restSegments);

export const buildBatchCallsPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.BATCH_CALLS, orgId, ...restSegments);

export const buildCallsManagementPath = (
  orgId: string,
  ...restSegments: string[]
): string =>
  buildOrgScopedPath(ROUTE_SEGMENT.CALLS_MANAGEMENT, orgId, ...restSegments);

export const resolveMenuItemUrl = (
  url: string | undefined,
  orgId: string
): string => {
  if (!url) return '/';

  return url.replace(ORG_ID_ROUTE_TOKEN, orgId);
};
